import React from "react";
import { Trans, useTranslation } from "react-i18next";
import { UploadProgressBar } from "src/ui/containers/dashboard/files/components/UploadProgressBar";
import { useFileUpload } from "src/ui/containers/dashboard/files/hooks/useFileUpload";
import { CustomAlert } from "src/ui/shared/CustomAlert/CustomAlert";
import { FileDropzone } from "src/ui/shared/form/FileDropzone";

import { useUploadContext } from "@dashboard/files/context/UploadContext";
import { CustomAlertSeverity } from "@shared/CustomAlert/CustomAlert";
import { Modal } from "@shared/Modal/Modal";

type Message = {
    severity: CustomAlertSeverity;
    text: string;
};

type Props = {
    groupId?: number;
    groupName?: string;
    open: boolean;
    onClose: () => void;
    onUploadStarted?: () => void;
    disabled?: boolean;
    showProgressBar?: boolean;
    hint?: React.ReactNode;
};

export const FileUploadModal = ({ showProgressBar = true, ...props }: Props) => {
    const { t } = useTranslation(["modals", "common"]);
    const inputId = React.useId();
    const { uploadStarted } = useUploadContext();
    const { onFilesSelected, availableSlots, isLimitReached, isLowOnSlots, validationError, clearValidationError } =
        useFileUpload({
            groupId: props.groupId,
            onUploadStarted: props.onUploadStarted,
        });

    const getMessage = (): Message | null => {
        if (validationError) {
            return { severity: "error", text: validationError };
        } else if (isLimitReached) {
            return { severity: "error", text: t("common:section.uploadField.fileCountLimitReached") };
        } else if (isLowOnSlots) {
            return {
                severity: "warning",
                text: t("common:section.uploadField.fileCountLimitWarning", { count: availableSlots }),
            };
        } else {
            return null;
        }
    };

    const message = getMessage();

    const onClose = () => {
        clearValidationError();
        props.onClose();
    };

    return (
        <Modal open={props.open} onClose={onClose} title={t("modals:uploadFile.title")}>
            <FileDropzone
                inputId={inputId}
                label={
                    props.groupName ? (
                        <Trans
                            ns="common"
                            i18nKey="section.uploadField.dropzoneWithDestination"
                            values={{ destination: props.groupName }}
                            components={{ u: <u /> }}
                        />
                    ) : (
                        <Trans ns="common" i18nKey="section.uploadField.dropzone" components={{ u: <u /> }} />
                    )
                }
                caption={t("common:section.uploadField.maxSize")}
                error={message?.severity === "error"}
                warning={message?.severity === "warning"}
                multiple
                onFilesSelected={onFilesSelected}
                disabled={props.disabled || uploadStarted}
            />
            {message ? <CustomAlert severity={message.severity}>{message.text}</CustomAlert> : null}
            {props.hint ? <CustomAlert severity="info">{props.hint}</CustomAlert> : null}
            {showProgressBar && uploadStarted ? <UploadProgressBar /> : null}
        </Modal>
    );
};
